import React, { useState, useMemo } from "react";
import { Maximize, Layers, Building2, Tag, ArrowUpDown } from "lucide-react";
import { MOCK_UNITS, UNIT_TYPES } from "../mock";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Slider } from "./ui/slider";

const fmt = (n) => n.toLocaleString("en-US");

const statusStyle = {
  "متاح": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "محجوز": "bg-amber-50 text-amber-700 border-amber-200",
  "مباع": "bg-rose-50 text-rose-700 border-rose-200",
};

const AvailableUnits = ({ units = MOCK_UNITS, onRegister }) => {
  const [type, setType] = useState("all");
  const [floor, setFloor] = useState("all");
  const [price, setPrice] = useState([2000000, 17000000]);
  const [sort, setSort] = useState("price-asc");

  const floors = useMemo(
    () => [...new Set(units.map((u) => u.floor))].sort((a, b) => a - b),
    [units]
  );

  const filtered = useMemo(() => {
    let list = units.filter((u) => {
      if (type !== "all" && u.type !== type) return false;
      if (floor !== "all" && String(u.floor) !== floor) return false;
      if (u.price < price[0] || u.price > price[1]) return false;
      return true;
    });
    const [key, dir] = sort.split("-");
    list = [...list].sort((a, b) => (dir === "asc" ? a[key] - b[key] : b[key] - a[key]));
    return list;
  }, [units, type, floor, price, sort]);

  return (
    <section id="units" className="py-24 bg-[#EFE6D6]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-[#BF9A5A] text-sm font-700">الوحدات المتاحة</span>
          <h2 className="font-display text-3xl lg:text-5xl font-700 text-[#241C15] mt-3">
            اختر وحدتك في البرج الطويل
          </h2>
          <p className="text-stone-600 mt-5">تصفّح الوحدات وسجّل اهتمامك بالوحدة المناسبة لك.</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-stone-200/60 mb-8 grid md:grid-cols-4 gap-5 items-end">
          <div>
            <label className="text-xs font-700 text-stone-500 mb-2 block">نوع الوحدة</label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-[#F3ECE0] border-stone-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                {UNIT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-700 text-stone-500 mb-2 block">الدور</label>
            <Select value={floor} onValueChange={setFloor}>
              <SelectTrigger className="bg-[#F3ECE0] border-stone-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">الكل</SelectItem>
                {floors.map((f) => <SelectItem key={f} value={String(f)}>الدور {f}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-700 text-stone-500 mb-2 block">
              نطاق السعر: {fmt(price[0])} - {fmt(price[1])} ريال
            </label>
            <Slider
              value={price}
              onValueChange={setPrice}
              min={2000000}
              max={17000000}
              step={100000}
              className="mt-4"
            />
          </div>

          <div>
            <label className="text-xs font-700 text-stone-500 mb-2 block">الترتيب</label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="bg-[#F3ECE0] border-stone-200"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="price-asc">السعر: الأقل أولاً</SelectItem>
                <SelectItem value="price-desc">السعر: الأعلى أولاً</SelectItem>
                <SelectItem value="area-asc">المساحة: الأصغر أولاً</SelectItem>
                <SelectItem value="area-desc">المساحة: الأكبر أولاً</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4 text-sm text-stone-500">
          <span className="flex items-center gap-1"><ArrowUpDown size={14} /> {filtered.length} وحدة</span>
        </div>

        {/* Cards */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-stone-500">لا توجد وحدات مطابقة للفلاتر.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((u) => (
              <div
                key={u.id}
                className="bg-white rounded-2xl border border-stone-200/60 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col"
              >
                <div className="bg-[#241C15] px-5 py-4 flex items-center justify-between">
                  <div>
                    <div className="text-white font-800 text-lg tracking-wide">{u.unitNumber || "—"}</div>
                    <div className="text-stone-400 text-xs mt-0.5">{u.type}</div>
                  </div>
                  <span className={`text-xs font-700 px-3 py-1 rounded-full border ${statusStyle[u.status] || ""}`}>
                    {u.status}
                  </span>
                </div>

                <div className="p-5 flex-1">
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <Maximize className="mx-auto text-[#BF9A5A]" size={18} />
                      <div className="font-700 text-[#241C15] mt-1">{u.area}</div>
                      <div className="text-[11px] text-stone-400">م²</div>
                    </div>
                    <div>
                      <Layers className="mx-auto text-[#BF9A5A]" size={18} />
                      <div className="font-700 text-[#241C15] mt-1">{u.floor}</div>
                      <div className="text-[11px] text-stone-400">الدور</div>
                    </div>
                    <div>
                      <Building2 className="mx-auto text-[#BF9A5A]" size={18} />
                      <div className="font-700 text-[#241C15] mt-1 text-xs">طويل</div>
                      <div className="text-[11px] text-stone-400">البرج</div>
                    </div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-stone-100 flex items-center gap-2">
                    <Tag className="text-[#BF9A5A]" size={18} />
                    <span className="text-2xl font-800 text-[#241C15]">{fmt(u.price)}</span>
                    <span className="text-sm text-stone-500">ريال</span>
                  </div>
                </div>

                <button
                  onClick={() => onRegister(u.unitNumber)}
                  disabled={u.status === "مباع"}
                  className="m-5 mt-0 py-3 rounded-xl bg-[#BF9A5A] hover:bg-[#a9853f] text-white font-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  سجّل اهتمامك
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AvailableUnits;
